import React, { useState, useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faUpload, faFolderPlus, faHome, faDownload, faEye, faEdit, faTrash,
  faFolder, faFolderOpen, faFilePdf, faFileWord, faFileExcel, faFileImage, 
  faFile, faTh, faList, faTimes, faExclamationTriangle, faLink, faSpinner,
  // Additional folder icons for customization
  faArchive, faBriefcase, faBook, faGraduationCap, faHeart, faStar,
  faCog, faLock, faCloud, faCamera, faMusic, faGamepad,
  faShoppingCart, faPalette, faLightbulb, faRocket, faCertificate,
  faAward, faTrophy, faGift, faBirthdayCake, faUtensils,
  faCoffee, faPizzaSlice, faPlane, faCar, faBicycle,
  faUmbrella, faGlobe, faMapMarkedAlt, faBuilding, faIndustry,
  faHospital, faSchool, faStore, faWarehouse, faHome as faHouseAlt,
  faChartLine, faChartBar, faDollarSign, faPercentage, faCalculator,
  faBell, faEnvelope, faPhone, faComments, faAddressBook,
  faClipboard, faTasks, faCalendar, faClock, faStopwatch,
  faUserMd, faCheck
} from '@fortawesome/free-solid-svg-icons';
import Layout from './Layout';
import './Settings.css';
import withAuth from '../utils/withAuth';
import { useFlashMessage } from './FlashMessage';
import Footer from './ui/Footer';
import HamsterFloatingButton from './HamsterFloatingButton';
import BackToTopButton from './BackToTopButton';
import * as personalFilesService from '../services/personalFilesService';
import * as docuwareService from '../services/docuwareService';
import knowledgeHubService from '../services/knowledgeHubService';
import logo from '../assets/images/IMT_logo.png';

function MyFiles() {
  const history = useHistory();
  const { showSuccess, showError, showInfo } = useFlashMessage();
  
  // Scroll to top on component mount
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // My Files state
  const [myFiles, setMyFiles] = useState([]);
  const [folders, setFolders] = useState([]);
  const [sickNotes, setSickNotes] = useState([]);
  const [sickNotesCount, setSickNotesCount] = useState(0);
  const [currentFolder, setCurrentFolder] = useState(null);
  const [viewMode, setViewMode] = useState('grid');
  const [searchQuery, setSearchQuery] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [previewFile, setPreviewFile] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(null);
  const [showFolderModal, setShowFolderModal] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [selectedFolderColor, setSelectedFolderColor] = useState('#ffc20c');
  const [selectedFolderIcon, setSelectedFolderIcon] = useState('faFolder');
  const [editMode, setEditMode] = useState(false);
  const [selectedFolders, setSelectedFolders] = useState([]);
  const [fileEditMode, setFileEditMode] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteModalData, setDeleteModalData] = useState({ type: '', items: [] });
  const [deleting, setDeleting] = useState(false);
  const [uploadStats, setUploadStats] = useState({ current: 0, total: 0 });
  const fileInputRef = useRef(null);

  // Color palette options (20 colors)
  const folderColors = [
    '#ffc20c', '#e74c3c', '#3498db', '#2ecc71', '#9b59b6',
    '#f39c12', '#e67e22', '#1abc9c', '#34495e', '#95a5a6',
    '#c0392b', '#8e44ad', '#2980b9', '#27ae60', '#f1c40f',
    '#d35400', '#16a085', '#7f8c8d', '#e91e63', '#00bcd4'
  ];

  // Icon options (50 diverse icons)
  const folderIcons = [
    { name: 'faFolder', icon: faFolder },
    { name: 'faFolderOpen', icon: faFolderOpen },
    { name: 'faArchive', icon: faArchive },
    { name: 'faBriefcase', icon: faBriefcase },
    { name: 'faBook', icon: faBook },
    { name: 'faGraduationCap', icon: faGraduationCap },
    { name: 'faHeart', icon: faHeart },
    { name: 'faStar', icon: faStar },
    { name: 'faCog', icon: faCog },
    { name: 'faLock', icon: faLock },
    { name: 'faCloud', icon: faCloud },
    { name: 'faCamera', icon: faCamera },
    { name: 'faMusic', icon: faMusic },
    { name: 'faGamepad', icon: faGamepad },
    { name: 'faShoppingCart', icon: faShoppingCart },
    { name: 'faPalette', icon: faPalette },
    { name: 'faLightbulb', icon: faLightbulb },
    { name: 'faRocket', icon: faRocket },
    { name: 'faCertificate', icon: faCertificate },
    { name: 'faAward', icon: faAward },
    { name: 'faTrophy', icon: faTrophy },
    { name: 'faGift', icon: faGift },
    { name: 'faBirthdayCake', icon: faBirthdayCake },
    { name: 'faUtensils', icon: faUtensils },
    { name: 'faCoffee', icon: faCoffee },
    { name: 'faPizzaSlice', icon: faPizzaSlice },
    { name: 'faPlane', icon: faPlane },
    { name: 'faCar', icon: faCar },
    { name: 'faBicycle', icon: faBicycle },
    { name: 'faUmbrella', icon: faUmbrella },
    { name: 'faGlobe', icon: faGlobe },
    { name: 'faMapMarkedAlt', icon: faMapMarkedAlt },
    { name: 'faBuilding', icon: faBuilding },
    { name: 'faIndustry', icon: faIndustry },
    { name: 'faHospital', icon: faHospital },
    { name: 'faSchool', icon: faSchool },
    { name: 'faStore', icon: faStore },
    { name: 'faWarehouse', icon: faWarehouse },
    { name: 'faHouseAlt', icon: faHouseAlt },
    { name: 'faChartLine', icon: faChartLine },
    { name: 'faChartBar', icon: faChartBar },
    { name: 'faDollarSign', icon: faDollarSign },
    { name: 'faPercentage', icon: faPercentage },
    { name: 'faCalculator', icon: faCalculator },
    { name: 'faBell', icon: faBell },
    { name: 'faEnvelope', icon: faEnvelope },
    { name: 'faPhone', icon: faPhone },
    { name: 'faComments', icon: faComments },
    { name: 'faAddressBook', icon: faAddressBook },
    { name: 'faClipboard', icon: faClipboard },
    { name: 'faTasks', icon: faTasks },
    { name: 'faCalendar', icon: faCalendar },
    { name: 'faClock', icon: faClock },
    { name: 'faStopwatch', icon: faStopwatch },
    { name: 'faUserMd', icon: faUserMd }
  ];

  // Helper function to get icon by name
  const getIconByName = (iconName) => {
    const iconObj = folderIcons.find(i => i.name === iconName);
    return iconObj ? iconObj.icon : faFolder;
  };

  // Helper function to reload files and folders
  const reloadFilesAndFolders = async () => {
    try {
      const [filesData, foldersData, sickNotesData] = await Promise.all([
        personalFilesService.listFiles(null), // Always load ALL files
        personalFilesService.listFolders(),
        docuwareService.listSickNotesForMyFiles()
      ]);
      setMyFiles(filesData);
      setFolders(foldersData);
      setSickNotes(sickNotesData);
      setSickNotesCount(sickNotesData.length);
    } catch (error) {
      console.error('Error reloading files and folders:', error);
    }
  };

  // Open in-app preview for a file (regular or sick note)
  const handlePreviewFile = async (file, isSickNote) => {
    const fileId = isSickNote ? (file.file_id || file.id) : file.id;
    const ext = file.file_name.split('.').pop().toLowerCase();

    // Map extension to preview type
    const imageMimeTypes = {
      png: 'image/png',
      jpg: 'image/jpeg',
      jpeg: 'image/jpeg',
      gif: 'image/gif',
      webp: 'image/webp',
      svg: 'image/svg+xml'
    };

    // Unsupported types open the modal with a download fallback (no fetch needed)
    if (ext !== 'pdf' && !imageMimeTypes[ext]) {
      setPreviewFile({ name: file.file_name, url: null, mimeType: 'other', id: fileId, isSickNote });
      return;
    }

    // Open the modal immediately in a loading state — the DocuWare fetch
    // takes a few seconds and the user should see the modal react instantly
    const mimeType = ext === 'pdf' ? 'application/pdf' : 'image';
    setPreviewFile({ name: file.file_name, url: null, mimeType, id: fileId, isSickNote, loading: true });

    try {
      setPreviewLoading(fileId);
      const blobData = isSickNote
        ? await docuwareService.fetchSickNoteBlob(fileId)
        : await personalFilesService.getFilePreview(fileId);

      // Re-type the blob so the browser renders it instead of downloading it
      const contentType = ext === 'pdf' ? 'application/pdf' : imageMimeTypes[ext];
      const url = window.URL.createObjectURL(new Blob([blobData], { type: contentType }));

      // Only populate if the modal is still open for this file; if the user
      // closed it mid-fetch, release the blob URL instead of leaking it
      setPreviewFile(prev => {
        if (prev && prev.id === fileId && prev.loading) {
          return { ...prev, url, loading: false };
        }
        window.URL.revokeObjectURL(url);
        return prev;
      });
    } catch (error) {
      setPreviewFile(prev => (prev && prev.id === fileId ? null : prev));
      showError('Failed to load preview');
    } finally {
      setPreviewLoading(null);
    }
  };

  // Close preview on Escape; revoke object URL on close and unmount
  useEffect(() => {
    if (!previewFile) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setPreviewFile(null);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    const url = previewFile.url;
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      if (url) {
        window.URL.revokeObjectURL(url);
      }
    };
  }, [previewFile]);

  // Load personal files and folders on mount
  useEffect(() => {
    const loadFilesAndFolders = async () => {
      try {
        const [filesData, foldersData, sickNotesData] = await Promise.all([
          personalFilesService.listFiles(null),
          personalFilesService.listFolders(),
          docuwareService.listSickNotesForMyFiles()
        ]);
        setMyFiles(filesData);
        setFolders(foldersData);
        setSickNotes(sickNotesData);
        setSickNotesCount(sickNotesData.length);
      } catch (error) {
        console.error('Error loading personal files and folders:', error);
      }
    };
    loadFilesAndFolders();
  }, []);

  return (
    <Layout>
      <div className="my-files-page-layout account-information-layout">
        <div className="account-information-wrapper">
          <div className="account-information-container">
            {/* Account Information Header */}
            <div className="account-information-header">
              <div className="header-logo">
                <img 
                  src={logo} 
                  alt="IMT Logo" 
                  className="header-logo-img" 
                  onClick={() => history.push('/dashboard')}
                  style={{ cursor: 'pointer' }}
                />
                <div className="header-titles">
                  <h1 className="hms-screen-title">My Files</h1>
                  <p className="account-subtitle">Manage your personal documents and files</p>
                </div>
              </div>
            </div>

            {/* Main Content Container */}
            <div className="account-information-content">

              {/* My Files Section - WITH LEFT SIDEBAR */}
              <div className="form-section my-files-section">
                <h2>My Files</h2>
                <p className="section-description">
                  Manage your personal documents and files.
                </p>

                {/* Main Layout: Sidebar + Content */}
                <div className="my-files-container">
                  {/* Left Sidebar - Folders */}
                  <div className="my-files-sidebar">
                    <div className="my-files-sidebar-header">
                      <h3>Folders</h3>
                      <div className="my-files-sidebar-actions">
                        <button
                          className={`my-files-btn-icon ${editMode ? 'active' : ''}`}
                          title={editMode ? "Done Editing" : "Edit Folders"}
                          onClick={() => {
                            setEditMode(!editMode);
                            setSelectedFolders([]);
                          }}
                        >
                          <FontAwesomeIcon icon={editMode ? faTimes : faEdit} />
                        </button>
                        <button
                          className="my-files-btn-icon"
                          title="Create Folder"
                          onClick={() => {
                            setNewFolderName('');
                            setShowFolderModal(true);
                          }}
                        >
                          <FontAwesomeIcon icon={faFolderPlus} />
                        </button>
                      </div>
                    </div>

                    {/* Delete Selected Folders Button - Above folder list */}
                    {editMode && selectedFolders.length > 0 && (
                      <div className="my-files-sidebar-delete-actions-top">
                        <button
                          className="my-files-delete-selected-btn"
                          onClick={() => {
                            setDeleteModalData({ 
                              type: 'folders', 
                              items: selectedFolders.map(name => {
                                // Handle special "Sick Notes" folder
                                if (name === 'Sick Notes') {
                                  return {
                                    name,
                                    icon: 'faUserMd',
                                    color: '#3498db'
                                  };
                                }
                                // Handle regular folders
                                const folder = folders.find(f => f.folder_name === name);
                                return {
                                  name,
                                  icon: folder?.icon || 'faFolder',
                                  color: folder?.color || '#ffc20c'
                                };
                              }) 
                            });
                            setShowDeleteModal(true);
                          }}
                          disabled={deleting}
                        >
                          <FontAwesomeIcon icon={faTrash} />
                          {deleting ? 'Deleting folders...' : `Delete Selected (${selectedFolders.length})`}
                        </button>
                      </div>
                    )}

                    <div className="my-files-sidebar-content">
                      <div
                        className={`my-files-sidebar-item ${currentFolder === null ? 'active' : ''}`}
                        onClick={() => {
                          setCurrentFolder(null);
                        }}
                        style={{ cursor: 'pointer' }}
                      >
                        <FontAwesomeIcon icon={faHome} className="my-files-sidebar-icon" />
                        <span className="my-files-sidebar-name">All Files</span>
                        <span className="my-files-sidebar-count">{myFiles.length + sickNotesCount}</span>
                      </div>

                      {sickNotesCount > 0 && (
                        <div
                          className={`my-files-sidebar-item ${currentFolder === 'Sick Notes' ? 'active' : ''}`}
                          onClick={() => {
                            if (!editMode) {
                              setCurrentFolder('Sick Notes');
                            }
                          }}
                          style={{ cursor: editMode ? 'default' : 'pointer' }}
                        >
                          {editMode && (
                            <input
                              type="checkbox"
                              className="my-files-custom-checkbox"
                              checked={selectedFolders.includes('Sick Notes')}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedFolders([...selectedFolders, 'Sick Notes']);
                                } else {
                                  setSelectedFolders(selectedFolders.filter(f => f !== 'Sick Notes'));
                                }
                              }}
                              onClick={(e) => e.stopPropagation()}
                            />
                          )}
                          <div className="my-files-sidebar-item-content">
                            <FontAwesomeIcon 
                              icon={faUserMd} 
                              className="my-files-sidebar-icon" 
                              style={{ color: '#3498db' }}
                            />
                            <span className="my-files-sidebar-name">Sick Notes</span>
                            <span className="my-files-sidebar-count">{sickNotesCount}</span>
                          </div>
                        </div>
                      )}

                      {folders.length > 0 && folders.map(folder => (
                        <div
                          key={folder.folder_name}
                          className={`my-files-sidebar-item ${currentFolder === folder.folder_name && !editMode ? 'active' : ''}`}
                          onClick={() => {
                            if (!editMode) {
                              setCurrentFolder(folder.folder_name);
                            }
                          }}
                          style={{ cursor: editMode ? 'default' : 'pointer' }}
                        >
                          {editMode && (
                            <input
                              type="checkbox"
                              className="my-files-custom-checkbox"
                              checked={selectedFolders.includes(folder.folder_name)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedFolders([...selectedFolders, folder.folder_name]);
                                } else {
                                  setSelectedFolders(selectedFolders.filter(f => f !== folder.folder_name));
                                }
                              }}
                              onClick={(e) => e.stopPropagation()}
                            />
                          )}
                          <div className="my-files-sidebar-item-content">
                            <FontAwesomeIcon 
                              icon={getIconByName(folder.icon || 'faFolder')} 
                              className="my-files-sidebar-icon" 
                              style={{ color: folder.color || '#ffc20c' }}
                            />
                            <span className="my-files-sidebar-name">{folder.folder_name}</span>
                            <span className="my-files-sidebar-count">{folder.file_count || 0}</span>
                          </div>
                        </div>
                      ))}

                      {folders.length === 0 && sickNotesCount === 0 && (
                        <div className="my-files-sidebar-empty">
                          <p>No folders yet</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Main Content Area */}
                  <div className="my-files-main">
                    <div className="my-files-toolbar">
                      <div className="my-files-toolbar-left">
                        <input
                          type="file"
                          ref={fileInputRef}
                          multiple
                          style={{ display: 'none' }}
                          onChange={async (e) => {
                            const files = Array.from(e.target.files);
                            
                            // Validate file count
                            if (files.length === 0) return;
                            if (files.length > 10) {
                              showError('Maximum 10 files per upload session');
                              if (fileInputRef.current) fileInputRef.current.value = '';
                              return;
                            }
                            
                            // Check if we're uploading to Sick Notes folder
                            const isSickNotesFolder = currentFolder === 'Sick Notes';
                            
                            // Validate file types for sick notes (PDF, JPG, PNG only)
                            if (isSickNotesFolder) {
                              const allowedExtensions = ['.pdf', '.jpg', '.jpeg', '.png'];
                              const invalidFiles = files.filter(f => {
                                const ext = '.' + f.name.split('.').pop().toLowerCase();
                                return !allowedExtensions.includes(ext);
                              });
                              if (invalidFiles.length > 0) {
                                showError(`Invalid file type(s) for sick notes. Only PDF, JPG, and PNG allowed: ${invalidFiles.map(f => f.name).join(', ')}`);
                                if (fileInputRef.current) fileInputRef.current.value = '';
                                return;
                              }
                            }
                            
                            // Validate each file size (10MB for sick notes, 50MB for regular files)
                            const maxSize = isSickNotesFolder ? 10485760 : 52428800; // 10MB or 50MB
                            const oversizedFiles = files.filter(f => f.size > maxSize);
                            if (oversizedFiles.length > 0) {
                              const limitText = isSickNotesFolder ? '10MB' : '50MB';
                              showError(`${oversizedFiles.length} file(s) exceed ${limitText} limit: ${oversizedFiles.map(f => f.name).join(', ')}`);
                              if (fileInputRef.current) fileInputRef.current.value = '';
                              return;
                            }
                            
                            try {
                              setUploading(true);
                              setUploadStats({ current: 0, total: files.length });
                              let successCount = 0;
                              let errorCount = 0;
                              const errors = [];
                              
                              // Upload files sequentially
                              for (let i = 0; i < files.length; i++) {
                                const file = files[i];
                                setUploadStats({ current: i + 1, total: files.length });
                                
                                try {
                                  if (isSickNotesFolder) {
                                    // Upload to sick notes cabinet
                                    await docuwareService.uploadSickNoteFromMyFiles(file, '');
                                    
                                    // Manual progress simulation for sick notes (no progress callback)
                                    setUploadProgress(prev => ({
                                      ...prev,
                                      [file.name]: 100
                                    }));
                                  } else {
                                    // Upload to personal files
                                    await personalFilesService.uploadFileComplete(
                                      file,
                                      currentFolder,
                                      (progress) => {
                                        setUploadProgress(prev => ({
                                          ...prev,
                                          [file.name]: progress
                                        }));
                                      }
                                    );
                                  }
                                  successCount++;
                                  
                                  // Show 100% completion for 300ms before moving to next file
                                  await new Promise(resolve => setTimeout(resolve, 300));
                                  
                                  // Remove from progress after successful upload
                                  setUploadProgress(prev => {
                                    const newProgress = { ...prev };
                                    delete newProgress[file.name];
                                    return newProgress;
                                  });
                                } catch (error) {
                                  errorCount++;
                                  errors.push({
                                    file: file.name,
                                    message: error.response?.data?.error || 'Upload failed'
                                  });
                                  
                                  // Brief pause even on error for visual continuity
                                  await new Promise(resolve => setTimeout(resolve, 200));
                                  
                                  // Remove from progress on error
                                  setUploadProgress(prev => {
                                    const newProgress = { ...prev };
                                    delete newProgress[file.name];
                                    return newProgress;
                                  });
                                }
                              }
                              
                              // Show results
                              if (successCount > 0) {
                                showSuccess(`${successCount} file${successCount > 1 ? 's' : ''} uploaded successfully`);
                              }
                              if (errorCount > 0) {
                                const errorMsg = errors.map(e => `${e.file}: ${e.message}`).join('; ');
                                showError(`Failed to upload ${errorCount} file${errorCount > 1 ? 's' : ''}: ${errorMsg}`);
                              }
                              
                              await reloadFilesAndFolders();
                            } catch (error) {
                              showError('Upload process failed');
                            } finally {
                              setUploading(false);
                              setUploadProgress({});
                              setUploadStats({ current: 0, total: 0 });
                              if (fileInputRef.current) fileInputRef.current.value = '';
                            }
                          }}
                        />
                        <button
                          className="my-files-btn my-files-btn-primary"
                          onClick={() => fileInputRef.current?.click()}
                          disabled={uploading}
                        >
                          <FontAwesomeIcon icon={faUpload} />
                          Upload File
                        </button>
                        
                        {/* Upload Progress - Next to Upload Button */}
                        {uploadStats.total > 0 && (
                          <div className="my-files-upload-progress">
                            <div className="my-files-upload-item">
                              <div className="my-files-upload-name">
                                {Object.keys(uploadProgress).length > 0 
                                  ? Object.keys(uploadProgress)[0]
                                  : 'Processing...'}
                              </div>
                              <div className="my-files-upload-bar-container">
                                <div className="my-files-upload-bar">
                                  <div 
                                    className="my-files-upload-progress-fill" 
                                    style={{ 
                                      width: Object.keys(uploadProgress).length > 0 
                                        ? `${Object.values(uploadProgress)[0]}%` 
                                        : '0%' 
                                    }}
                                  ></div>
                                </div>
                                <div className="my-files-upload-counter">
                                  ({uploadStats.current}/{uploadStats.total})
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="my-files-toolbar-right">
                        <button
                          className={`my-files-btn-icon ${fileEditMode ? 'active' : ''}`}
                          onClick={() => {
                            setFileEditMode(!fileEditMode);
                            setSelectedFiles([]);
                          }}
                          title={fileEditMode ? 'Done Editing' : 'Edit Files'}
                        >
                          <FontAwesomeIcon icon={fileEditMode ? faTimes : faEdit} />
                        </button>
                        <input
                          type="text"
                          className="my-files-search"
                          placeholder="Search files..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                        />
                        <button
                          className="my-files-btn"
                          onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}
                        >
                          <FontAwesomeIcon icon={viewMode === 'grid' ? faList : faTh} />
                        </button>
                      </div>
                    </div>

                    {currentFolder && (
                      <div className="my-files-breadcrumb">
                        <span className="my-files-breadcrumb-item" onClick={() => {
                          setCurrentFolder(null);
                        }}>
                          <FontAwesomeIcon icon={faHome} /> All Files
                        </span>
                        <span className="my-files-breadcrumb-separator">/</span>
                        <span className="my-files-breadcrumb-item active">
                          {currentFolder}
                        </span>
                      </div>
                    )}

                    {/* Delete Selected Files Button */}
                    {fileEditMode && selectedFiles.length > 0 && (
                      <div className="my-files-bulk-delete-container">
                        <button
                          className="my-files-delete-selected-btn"
                          onClick={() => {
                            const isSickNotesView = currentFolder === 'Sick Notes';
                            let filesToDelete = [];
                            
                            if (isSickNotesView) {
                              // Sick Notes view - all selected are sick notes
                              filesToDelete = sickNotes
                                .filter(f => selectedFiles.includes(f.file_id))
                                .map(f => ({ 
                                  id: f.file_id, 
                                  name: f.file_name,
                                  isSickNote: true
                                }));
                            } else if (currentFolder === null) {
                              // All Files view - need to check both regular files and sick notes
                              const regularFiles = myFiles
                                .filter(f => selectedFiles.includes(f.id))
                                .map(f => ({ 
                                  id: f.id, 
                                  name: f.file_name,
                                  isSickNote: false
                                }));
                              
                              const sickFiles = sickNotes
                                .filter(f => selectedFiles.includes(f.file_id))
                                .map(f => ({ 
                                  id: f.file_id, 
                                  name: f.file_name,
                                  isSickNote: true
                                }));
                              
                              filesToDelete = [...regularFiles, ...sickFiles];
                            } else {
                              // Specific folder view - all are regular files
                              filesToDelete = myFiles
                                .filter(f => selectedFiles.includes(f.id))
                                .map(f => ({ 
                                  id: f.id, 
                                  name: f.file_name,
                                  isSickNote: false
                                }));
                            }
                            
                            setDeleteModalData({ 
                              type: 'files', 
                              items: filesToDelete
                            });
                            setShowDeleteModal(true);
                          }}
                          disabled={deleting}
                        >
                          <FontAwesomeIcon icon={faTrash} />
                          {deleting ? 'Deleting files...' : `Delete Selected (${selectedFiles.length})`}
                        </button>
                      </div>
                    )}

                    <div className={viewMode === 'grid' ? 'my-files-grid' : 'my-files-list'}>
                      {(() => {
                        // Determine which files to display
                        let files;
                        let isSickNotesView = false;
                        
                        if (currentFolder === 'Sick Notes') {
                          // Display only sick notes
                          isSickNotesView = true;
                          files = sickNotes.filter(f => {
                            const searchMatch = searchQuery ? f.file_name.toLowerCase().includes(searchQuery.toLowerCase()) : true;
                            return searchMatch;
                          });
                        } else if (currentFolder === null) {
                          // "All Files" view - show both regular files and sick notes
                          const regularFiles = myFiles.filter(f => {
                            const searchMatch = searchQuery ? f.file_name.toLowerCase().includes(searchQuery.toLowerCase()) : true;
                            return searchMatch;
                          });
                          
                          const filteredSickNotes = sickNotes.filter(f => {
                            const searchMatch = searchQuery ? f.file_name.toLowerCase().includes(searchQuery.toLowerCase()) : true;
                            return searchMatch;
                          }).map(f => ({ ...f, isSickNote: true })); // Mark as sick note
                          
                          files = [...regularFiles, ...filteredSickNotes];
                        } else {
                          // Display regular files for specific folder
                          files = myFiles.filter(f => {
                            const folderMatch = f.folder_name === currentFolder;
                            const searchMatch = searchQuery ? f.file_name.toLowerCase().includes(searchQuery.toLowerCase()) : true;
                            return folderMatch && searchMatch;
                          });
                        }

                        const getFileIcon = (fileName) => {
                          const ext = fileName.split('.').pop().toLowerCase();
                          if (['pdf'].includes(ext)) return faFilePdf;
                          if (['doc', 'docx'].includes(ext)) return faFileWord;
                          if (['xls', 'xlsx'].includes(ext)) return faFileExcel;
                          if (['jpg', 'jpeg', 'png', 'gif'].includes(ext)) return faFileImage;
                          return faFile;
                        };

                        return files.length > 0 ? files.map(file => {
                          // Check if this is a sick note (either in Sick Notes view or marked as sick note in All Files)
                          const isFileSickNote = isSickNotesView || file.isSickNote;
                          
                          return (
                          <div key={`file-${file.id || file.file_id}`} className="my-files-item">
                            {fileEditMode && (
                              <input
                                type="checkbox"
                                className="my-files-custom-checkbox"
                                checked={selectedFiles.includes(file.id || file.file_id)}
                                onChange={(e) => {
                                  const fileId = file.id || file.file_id;
                                  if (e.target.checked) {
                                    setSelectedFiles([...selectedFiles, fileId]);
                                  } else {
                                    setSelectedFiles(selectedFiles.filter(id => id !== fileId));
                                  }
                                }}
                                onClick={(e) => e.stopPropagation()}
                              />
                            )}
                            <FontAwesomeIcon icon={getFileIcon(file.file_name)} className="my-files-item-icon" />
                            <div className="my-files-item-name">
                              {file.file_name}
                              {file.has_active_leave_requests && (
                                <span 
                                  className="sick-note-linked-badge" 
                                  title="This sick note is linked to an active leave request and cannot be deleted until the leave request is cancelled"
                                >
                                  <FontAwesomeIcon icon={faLink} style={{ marginRight: '4px' }} />
                                  Linked
                                </span>
                              )}
                            </div>
                            <div className="my-files-item-meta">
                              <span className="my-files-item-size">{(file.file_size / 1024).toFixed(1)} KB</span>
                              {file.date_uploaded && (
                                <span className="my-files-item-date">
                                  {new Date(file.date_uploaded).toLocaleDateString('en-US', { 
                                    year: 'numeric', 
                                    month: 'short', 
                                    day: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })}
                                </span>
                              )}
                            </div>
                            {!fileEditMode && (
                              <div className="my-files-item-actions" onClick={(e) => e.stopPropagation()}>
                              <button
                                className="my-files-action-btn"
                                title="Preview"
                                disabled={previewLoading !== null}
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  await handlePreviewFile(file, isFileSickNote);
                                }}
                              >
                                <FontAwesomeIcon icon={faEye} />
                              </button>
                              <button
                                className="my-files-action-btn"
                                title="Download"
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  try {
                                    if (isFileSickNote) {
                                      // Download sick note using DocuWare service
                                      await docuwareService.downloadSickNote(file.file_id || file.id, file.file_name);
                                    } else {
                                      // Download regular file
                                      await personalFilesService.downloadFile(file.id, file.file_name);
                                    }
                                    showSuccess('Download started');
                                  } catch (error) {
                                    showError('Failed to download file');
                                  }
                                }}
                              >
                                <FontAwesomeIcon icon={faDownload} />
                              </button>
                              {!isFileSickNote && (
                                <button
                                  className="my-files-action-btn"
                                  title="Send to Knowledge Hub"
                                  onClick={async (e) => {
                                    e.stopPropagation();
                                    try {
                                      const response = await personalFilesService.downloadFile(file.id, file.file_name);
                                      const blob = new Blob([response]);
                                      const fileObj = new File([blob], file.file_name, { type: blob.type });
                                      await knowledgeHubService.uploadDocument(fileObj);
                                      showSuccess('File sent to Knowledge Hub for processing');
                                    } catch (error) {
                                      showError('Failed to send file to Knowledge Hub');
                                    }
                                  }}
                                >
                                  <FontAwesomeIcon icon={faBook} />
                                </button>
                              )}
                              <button
                                className="my-files-action-btn danger"
                                title="Delete"
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  setDeleteModalData({ 
                                    type: 'files', 
                                    items: [{ id: file.id || file.file_id, name: file.file_name, isSickNote: isFileSickNote }] 
                                  });
                                  setShowDeleteModal(true);
                                }}
                              >
                                <FontAwesomeIcon icon={faTrash} />
                              </button>
                            </div>
                            )}
                          </div>
                        )}) : (
                          <div className="my-files-empty">
                            <FontAwesomeIcon icon={faFolderOpen} />
                            <div className="my-files-empty-title">No files yet</div>
                            <div className="my-files-empty-text">
                              {isSickNotesView ? 'No sick notes uploaded yet' : 'Upload your first file to get started'}
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              </div>

              {/* Folder Creation Modal */}
              {showFolderModal && (
                <div className="modal-overlay" onClick={() => setShowFolderModal(false)}>
                  <div className="modal-content my-files-modal" onClick={(e) => e.stopPropagation()}>
                    <div className="modal-header">
                      <h3>Create New Folder</h3>
                      <button
                        className="modal-close"
                        onClick={() => setShowFolderModal(false)}
                      >
                        <FontAwesomeIcon icon={faTimes} />
                      </button>
                    </div>
                    <div className="modal-body">
                      <div className="form-group">
                        <label>Folder Name</label>
                        <input
                          type="text"
                          className="form-control"
                          value={newFolderName}
                          onChange={(e) => setNewFolderName(e.target.value)}
                          placeholder="Enter folder name"
                          autoFocus
                          onKeyPress={(e) => {
                            if (e.key === 'Enter' && newFolderName.trim()) {
                              (async () => {
                                const trimmedName = newFolderName.trim();
                                
                                // Prevent creating "Sick Notes" folder manually
                                if (trimmedName.toLowerCase() === 'sick notes') {
                                  showError('Sick Notes is a system folder and will be created through Sick Leave requests');
                                  return;
                                }
                                
                                try {
                                  await personalFilesService.createFolder(trimmedName, selectedFolderColor, selectedFolderIcon);
                                  showSuccess(`Folder "${trimmedName}" created successfully`);
                                  await reloadFilesAndFolders();
                                  setShowFolderModal(false);
                                  setNewFolderName('');
                                  setSelectedFolderColor('#ffc20c');
                                  setSelectedFolderIcon('faFolder');
                                } catch (error) {
                                  showError(error.response?.data?.error || 'Failed to create folder');
                                }
                              })();
                            }
                          }}
                        />
                      </div>

                      {/* Color Palette Selector */}
                      <div className="form-group">
                        <label>Folder Color</label>
                        <div className="folder-color-palette">
                          {folderColors.map((color) => (
                            <div
                              key={color}
                              className={`color-circle ${selectedFolderColor === color ? 'selected' : ''}`}
                              style={{ backgroundColor: color }}
                              onClick={() => setSelectedFolderColor(color)}
                              title={color}
                            >
                              {selectedFolderColor === color && (
                                <FontAwesomeIcon icon={faCheck} className="color-circle-check" />
                              )}
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Icon Grid Selector */}
                      <div className="form-group">
                        <label>Folder Icon</label>
                        <div className="folder-icon-grid">
                          {folderIcons.map((iconObj) => (
                            <div
                              key={iconObj.name}
                              className={`icon-option ${selectedFolderIcon === iconObj.name ? 'selected' : ''}`}
                              onClick={() => setSelectedFolderIcon(iconObj.name)}
                              title={iconObj.name}
                            >
                              <FontAwesomeIcon icon={iconObj.icon} />
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                    <div className="modal-footer">
                      <button
                        className="btn btn-secondary"
                        onClick={() => setShowFolderModal(false)}
                      >
                        Cancel
                      </button>
                      <button
                        className="btn btn-primary"
                        onClick={async () => {
                          const trimmedName = newFolderName.trim();
                          if (trimmedName) {
                            // Prevent creating "Sick Notes" folder manually
                            if (trimmedName.toLowerCase() === 'sick notes') {
                              showError('Sick Notes is a system folder and will be created through Sick Leave requests');
                              return;
                            }
                            
                            try {
                              await personalFilesService.createFolder(trimmedName, selectedFolderColor, selectedFolderIcon);
                              showSuccess(`Folder "${trimmedName}" created successfully`);
                              await reloadFilesAndFolders();
                              setShowFolderModal(false);
                              setNewFolderName('');
                              setSelectedFolderColor('#ffc20c');
                              setSelectedFolderIcon('faFolder');
                            } catch (error) {
                              showError(error.response?.data?.error || 'Failed to create folder');
                            }
                          }
                        }}
                        disabled={!newFolderName.trim()}
                      >
                        Create Folder
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Delete Confirmation Modal (Folders & Files) */}
              {showDeleteModal && deleteModalData.items.length > 0 && (
                <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
                  <div className="modal-content my-files-modal delete-modal" onClick={(e) => e.stopPropagation()}>
                    <div className="modal-header delete-modal-header">
                      <h3>Delete {deleteModalData.type === 'folders' ? 'Folders' : 'Files'}</h3>
                      <button
                        className="modal-close"
                        onClick={() => setShowDeleteModal(false)}
                      >
                        <FontAwesomeIcon icon={faTimes} />
                      </button>
                    </div>
                    <div className="modal-body">
                      <div className="delete-modal-warning">
                        <FontAwesomeIcon icon={faExclamationTriangle} className="delete-modal-warning-icon" />
                        <div className="delete-modal-warning-content">
                          <h4>
                            Are you sure you want to delete {deleteModalData.items.length}{' '}
                            {deleteModalData.type === 'folders' 
                              ? (deleteModalData.items.length === 1 ? 'folder' : 'folders')
                              : (deleteModalData.items.length === 1 ? 'file' : 'files')
                            }?
                          </h4>
                          <p>
                            {deleteModalData.type === 'folders' 
                              ? 'Only empty folders will be deleted. This action cannot be undone.'
                              : 'This action cannot be undone.'
                            }
                          </p>
                        </div>
                      </div>
                      <div className="delete-modal-folder-list">
                        <strong>{deleteModalData.type === 'folders' ? 'Folders' : 'Files'} to delete:</strong>
                        <ul>
                          {deleteModalData.items.map((item, index) => (
                            <li key={item.id || item.name || index}>
                              <FontAwesomeIcon 
                                icon={deleteModalData.type === 'folders' ? getIconByName(item.icon) : faFile}
                                style={deleteModalData.type === 'folders' && item.color ? { color: item.color } : {}}
                              />
                              {item.name}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                    <div className="modal-footer">
                      <button
                        className="btn btn-secondary"
                        onClick={() => setShowDeleteModal(false)}
                      >
                        Cancel
                      </button>
                      <button
                        className="btn btn-danger"
                        disabled={deleting}
                        onClick={async () => {
                          setShowDeleteModal(false);
                          setDeleting(true);
                          let successCount = 0;
                          let errorCount = 0;
                          const errors = [];
                          
                          if (deleteModalData.type === 'folders') {
                            // Delete folders
                            for (const item of deleteModalData.items) {
                              try {
                                if (item.name === 'Sick Notes') {
                                  // Check if Sick Notes folder has files
                                  if (sickNotes.length > 0) {
                                    errorCount++;
                                    errors.push({
                                      name: item.name,
                                      message: `Folder contains ${sickNotes.length} file(s). Please delete all files before deleting the folder.`
                                    });
                                    continue;
                                  }
                                  // Folder is empty, no need to delete (it's virtual)
                                  successCount++;
                                } else {
                                  // Delete regular folder (backend will check if empty)
                                  await personalFilesService.deleteFolder(item.name);
                                  successCount++;
                                }
                                if (currentFolder === item.name) {
                                  setCurrentFolder(null);
                                }
                              } catch (error) {
                                errorCount++;
                                errors.push({
                                  name: item.name,
                                  message: error.response?.data?.error || 'Unknown error'
                                });
                              }
                            }
                            
                            // Show results
                            if (successCount > 0) {
                              showSuccess(`${successCount} ${successCount === 1 ? 'folder' : 'folders'} deleted successfully`);
                            }
                            if (errorCount > 0) {
                              const errorMsg = errors.map(e => `${e.name}: ${e.message}`).join(', ');
                              showError(`Failed to delete ${errorCount} ${errorCount === 1 ? 'folder' : 'folders'}: ${errorMsg}`);
                            }
                            
                            // Reset state
                            setSelectedFolders([]);
                            setEditMode(false);
                          } else {
                            // Delete files (regular or sick notes)
                            let hasLinkedLeaveError = false;
                            let linkedLeaveDetails = null;
                            
                            for (const item of deleteModalData.items) {
                              try {
                                if (item.isSickNote) {
                                  await docuwareService.deleteSickNote(item.id);
                                } else {
                                  await personalFilesService.deleteFile(item.id);
                                }
                                successCount++;
                              } catch (error) {
                                errorCount++;
                                
                                // Check if this is a linked leave request error
                                if (error.response?.data?.linked_leave_requests && error.response?.data?.can_delete === false) {
                                  hasLinkedLeaveError = true;
                                  linkedLeaveDetails = {
                                    fileName: item.name,
                                    message: error.response.data.message,
                                    linkedRequests: error.response.data.linked_leave_requests
                                  };
                                  errors.push({
                                    name: item.name,
                                    message: error.response.data.message || error.response.data.error
                                  });
                                } else {
                                  errors.push({
                                    name: item.name,
                                    message: error.response?.data?.error || 'Unknown error'
                                  });
                                }
                              }
                            }
                            
                            // Show results
                            if (successCount > 0) {
                              showSuccess(`${successCount} ${successCount === 1 ? 'file' : 'files'} deleted successfully`);
                            }
                            
                            if (errorCount > 0) {
                              if (hasLinkedLeaveError && linkedLeaveDetails) {
                                // Show detailed error for linked leave requests
                                const requestDetails = linkedLeaveDetails.linkedRequests
                                  .map(req => {
                                    const startDate = new Date(req.start_date).toLocaleDateString();
                                    const endDate = new Date(req.end_date).toLocaleDateString();
                                    return `Leave Request #${req.id}: ${startDate} - ${endDate} (${req.status})`;
                                  })
                                  .join('\n');
                                
                                showError(
                                  `Cannot delete "${linkedLeaveDetails.fileName}"\n\n` +
                                  `${linkedLeaveDetails.message}\n\n` +
                                  `Linked Leave Requests:\n${requestDetails}`
                                );
                              } else {
                                // Show generic error for other failures
                                const errorMsg = errors.map(e => `${e.name}: ${e.message}`).join(', ');
                                showError(`Failed to delete ${errorCount} ${errorCount === 1 ? 'file' : 'files'}: ${errorMsg}`);
                              }
                            }
                            
                            // Reset state
                            setSelectedFiles([]);
                            setFileEditMode(false);
                          }
                          
                          // Reload files and folders
                          await reloadFilesAndFolders();
                          setDeleting(false);
                        }}
                      >
                        <FontAwesomeIcon icon={faTrash} />
                        {deleting 
                          ? `Deleting ${deleteModalData.type === 'folders' ? 'folders' : 'files'}...`
                          : `Delete ${deleteModalData.type === 'folders' ? 'Folders' : 'Files'}`
                        }
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* File Preview Modal */}
              {previewFile && (
                <div className="modal-overlay" onClick={() => setPreviewFile(null)}>
                  <div className="modal-content preview-modal" onClick={(e) => e.stopPropagation()}>
                    <div className="modal-header">
                      <h3>{previewFile.name}</h3>
                      <button
                        className="modal-close"
                        onClick={() => setPreviewFile(null)}
                      >
                        <FontAwesomeIcon icon={faTimes} />
                      </button>
                    </div>
                    <div className="modal-body preview-modal-body">
                      {previewFile.loading && (
                        <div className="preview-modal-unavailable">
                          <FontAwesomeIcon icon={faSpinner} spin className="preview-modal-unavailable-icon" />
                          <p>Loading preview...</p>
                        </div>
                      )}
                      {!previewFile.loading && previewFile.mimeType === 'application/pdf' && (
                        <iframe
                          src={previewFile.url}
                          title={previewFile.name}
                          className="preview-modal-frame"
                        />
                      )}
                      {!previewFile.loading && previewFile.mimeType === 'image' && (
                        <img
                          src={previewFile.url}
                          alt={previewFile.name}
                          className="preview-modal-image"
                        />
                      )}
                      {previewFile.mimeType === 'other' && (
                        <div className="preview-modal-unavailable">
                          <FontAwesomeIcon icon={faFile} className="preview-modal-unavailable-icon" />
                          <p>Preview not available for this file type</p>
                          <button
                            className="my-files-btn my-files-btn-primary"
                            onClick={async () => {
                              try {
                                if (previewFile.isSickNote) {
                                  await docuwareService.downloadSickNote(previewFile.id, previewFile.name);
                                } else {
                                  await personalFilesService.downloadFile(previewFile.id, previewFile.name);
                                }
                                showSuccess('Download started');
                              } catch (error) {
                                showError('Failed to download file');
                              }
                            }}
                          >
                            <FontAwesomeIcon icon={faDownload} />
                            Download
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

            </div>
          </div>
        </div>

        {/* Hamster Chat Integration */}
        <HamsterFloatingButton />
        
        {/* Back to Top Button */}
        <BackToTopButton />
        
        {/* HMS Footer */}
        <Footer />
      </div>
    </Layout>
  );
}

export default withAuth(MyFiles);

